import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, RadioCards, Select } from '@/ds'
import { useAsync } from '@/lib/useAsync'
import { ageOn, checkinService, fieldRules, stepErrors } from '@/services/checkin'
import type { FieldError, GuestData, GuestField } from '@/services/checkin'
import type { Stay } from '@/services/reserva'

export type FieldErrors = Partial<Record<GuestField, FieldError>>

const LEGACY = 'v2.legacy.stepper.collectDataForm'

/** Revalida los campos que ya mostraban error al cambiar datos, también cuando el navegador autocompleta. */
export function errorsAfterGuestChange(errors: FieldErrors, patch: Partial<GuestData>, stay: Pick<Stay, 'id' | 'guestCode' | 'serverPath' | 'dates' | 'registration'>, guest: GuestData): FieldErrors {
  const changed = Object.keys(patch) as GuestField[]
  const fields = [...new Set([...Object.keys(errors) as GuestField[], ...changed])]
  if (!fields.length) return errors
  const validation = stepErrors(fields, stay, { ...guest, ...patch })
  const next = { ...errors }
  for (const field of fields) {
    if (validation[field]) next[field] = validation[field]
    else delete next[field]
  }
  return next
}

/** Mensaje de error de la app actual para cada campo (v2.legacy, textos ya traducidos). */
export function errorText(t: (k: string) => string, field: GuestField, error: FieldError | undefined): string | undefined {
  if (!error) return undefined
  if (error === 'invalid' && ['identifier', 'email', 'supportNumber', 'name', 'surname1', 'surname2'].includes(field)) return t(`${LEGACY}.errors.${field}.invalid`)
  if (error === 'invalid') return t('v2.legacy.validations.Invalid')
  switch (field) {
    case 'identifierTypeId':
    case 'countryId':
    case 'provinceId':
    case 'bornDate':
    case 'sexId':
      return t(`${LEGACY}.errors.${field}`)
    case 'municipalityId':
      return t(`${LEGACY}.errors.municipality.required`)
    case 'relationship':
      return undefined
    default:
      return t(`${LEGACY}.errors.${field}.required`)
  }
}

interface FieldsProps {
  stay: Pick<Stay, 'id' | 'guestCode' | 'serverPath' | 'dates' | 'registration'>
  guest: GuestData
  fields: GuestField[]
  errors: FieldErrors
  /** Campos dudosos de la lectura automática: aviso, no error (bundle 3b) */
  doubtful?: GuestField[]
  onChange: (patch: Partial<GuestData>) => void
  onBlurField?: (field: GuestField) => void
  /** Nombre del titular para "Relación con Marta" */
  holderName?: string | null
  /** Identidad del formulario que acaba de abrirse; enfoca su primer campo visible una sola vez. */
  focusRequest?: string | undefined
}

/** Campos del parte de viajeros en el orden y con las etiquetas del bundle; reglas de visibilidad portadas (rules.ts). */
export function GuestFields({ stay, guest, fields, errors, doubtful = [], onChange, onBlurField, holderName, focusRequest }: FieldsProps) {
  const { t } = useTranslation()
  const rootRef = useRef<HTMLDivElement>(null)
  const focusedRequest = useRef<string | undefined>(undefined)
  const rules = fieldRules(stay, guest)
  const cat = stay.registration.catalogs
  const show = (f: GuestField) => fields.includes(f) && rules[f].visible
  const err = (f: GuestField) => errorText(t, f, errors[f])
  const warn = (f: GuestField) => (doubtful.includes(f) && !guest[f] ? t('v2.checkin.revision.dudoso') : undefined)
  const blur = (f: GuestField) => () => onBlurField?.(f)
  const text = (f: Exclude<GuestField, 'relationship'>, label: string, extra: Partial<Parameters<typeof Input>[0]> = {}) =>
    show(f) ? (
      <Input
        key={f}
        label={label}
        value={guest[f]}
        onChange={(e) => onChange({ [f]: e.target.value })}
        onBlur={blur(f)}
        {...(err(f) ? { error: err(f) } : warn(f) ? { error: warn(f) } : {})}
        className={warn(f) && !err(f) ? 'checkin__field--warning' : undefined}
        {...extra}
      />
    ) : null

  const [municipalities] = useAsync(`mun:${guest.provinceId}`, (signal) => (show('municipalityId') && guest.provinceId ? checkinService.getMunicipalities(stay, guest.provinceId, signal) : Promise.resolve([])))
  const age = ageOn(guest.bornDate)
  const adults = stay.registration.travelers.filter((tr) => tr.isAdult)
  const childOptions = cat.relationships.filter((r) => r.type === 'menor' || r.type === 'otro')
  const allowedAdult = cat.relationships.find((r) => r.id === guest.relationship?.childRelationshipId)?.optionA.split(',').filter(Boolean) ?? []
  const adultOptions = cat.relationships.filter((r) => r.type !== 'menor' && (allowedAdult.length === 0 || allowedAdult.includes(r.id)))
  const rel = guest.relationship ?? { responsibleAdultId: '', childRelationshipId: '', adultRelationshipId: '' }

  useEffect(() => {
    if (!focusRequest || focusedRequest.current === focusRequest) return
    const firstVisible = fields.find((field) => rules[field].visible)
    if (!firstVisible) return

    // Esperamos a que los selectores condicionales y el menú de país estén montados.
    const timer = window.setTimeout(() => {
      const field = rootRef.current?.querySelector<HTMLElement>(`[data-checkin-field="${firstVisible}"]`)
      const target = field?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), button:not([disabled])')
      if (!target) return
      focusedRequest.current = focusRequest
      target.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
      target.focus({ preventScroll: true })
    }, 0)
    return () => window.clearTimeout(timer)
  }, [fields, focusRequest, rules])

  const out: Record<GuestField, React.ReactNode> = {
    name: text('name', t('v2.checkin.datos.nombre'), { autoComplete: 'given-name' }),
    surname1: text('surname1', t(`${LEGACY}.fields.surname1`), { help: t('v2.checkin.datos.apellidosAyuda'), autoComplete: 'family-name' }),
    surname2: text('surname2', t(`${LEGACY}.fields.surname2`)),
    email: text('email', t('v2.checkin.datos.correo'), { type: 'email', inputMode: 'email', autoComplete: 'email', help: t(checkinService.notices.contractByEmail ? 'v2.checkin.datos.correoAyuda' : 'v2.checkin.datos.correoAyudaNeutra') }),
    identifierTypeId: show('identifierTypeId') ? (
      <Select key="identifierTypeId" label={t(`${LEGACY}.fields.identifierTypeId`)} options={cat.identifierTypes.map((o) => ({ id: o.id, label: o.name }))} value={guest.identifierTypeId} onChange={(v) => onChange({ identifierTypeId: v })} placeholder={t(`${LEGACY}.placeholders.document`)} {...(err('identifierTypeId') ? { error: err('identifierTypeId') } : {})} />
    ) : null,
    identifier: text('identifier', t(`${LEGACY}.fields.identifier`), { className: 'tnum', autoComplete: 'off' }),
    supportNumber: text('supportNumber', t(`${LEGACY}.fields.supportNumber`), { autoCapitalize: 'characters', autoComplete: 'off', onChange: (event) => onChange({ supportNumber: event.target.value.toUpperCase() }) }),
    bornDate: text('bornDate', t(`${LEGACY}.fields.bornDate`), { type: 'date', max: new Date().toISOString().slice(0, 10), autoComplete: 'bday' }),
    address: text('address', t('v2.checkin.ley.direccion'), { help: t('v2.checkin.ley.direccionAyuda'), autoComplete: 'street-address' }),
    postalCode: text('postalCode', t('v2.checkin.ley.cp'), { inputMode: 'numeric', autoComplete: 'postal-code' }),
    city: text('city', t('v2.checkin.ley.ciudad'), { autoComplete: 'address-level2' }),
    countryId: show('countryId') ? (
      <Select key="countryId" label={t('v2.checkin.ley.pais')} options={cat.countries.map((o) => ({ id: o.id, label: o.name }))} value={guest.countryId} onChange={(v) => onChange({ countryId: v, provinceId: '', municipalityId: '' })} placeholder={t(`${LEGACY}.placeholders.country`)} searchable searchLabel={t('v2.ds.select.buscarPais')} searchPlaceholder={t('v2.ds.select.buscarPais')} noResultsText={t('v2.ds.select.sinResultados')} {...(err('countryId') ? { error: err('countryId') } : {})} />
    ) : null,
    provinceId: show('provinceId') ? (
      <Select key="provinceId" label={t(`${LEGACY}.fields.provinceId`)} options={cat.provinces.map((o) => ({ id: o.id, label: o.name }))} value={guest.provinceId} onChange={(v) => onChange({ provinceId: v, municipalityId: '' })} placeholder={t(`${LEGACY}.placeholders.province`)} {...(err('provinceId') ? { error: err('provinceId') } : {})} />
    ) : null,
    municipalityId: show('municipalityId') ? (
      <Select
        key="municipalityId"
        label={t(`${LEGACY}.fields.municipality`)}
        options={(municipalities.kind === 'ready' ? municipalities.value : []).map((o) => ({ id: o.id, label: o.name }))}
        value={guest.municipalityId}
        onChange={(v) => onChange({ municipalityId: v })}
        placeholder={municipalities.kind === 'loading' && guest.provinceId ? t('v2.comun.unMomento') : t(`${LEGACY}.placeholders.municipality`)}
        disabled={!guest.provinceId || municipalities.kind === 'loading'}
        {...(err('municipalityId') ? { error: err('municipalityId') } : municipalities.kind === 'error' ? { error: t('v2.legacy.error.tryAgainOrContactUs') } : {})}
      />
    ) : null,
    sexId: show('sexId') ? (
      <RadioCards key="sexId" label={t('v2.checkin.ley.sexo')} labelNote={t('v2.checkin.ley.exigeRegistro')} columns={2} options={cat.sexes.map((o) => ({ id: o.id, label: o.name }))} value={guest.sexId || null} onChange={(v) => onChange({ sexId: v })} {...(err('sexId') ? { error: err('sexId') } : {})} />
    ) : null,
    relationship: fields.includes('relationship') ? (
      rules.relationship.visible ? (
        <div key="relationship" className="checkin__group">
          <Select label={t('v2.legacy.labels.Child_responsible_adult')} options={adults.map((a) => ({ id: a.id, label: `${a.name} ${a.surname}`.trim() }))} value={rel.responsibleAdultId} onChange={(v) => onChange({ relationship: { ...rel, responsibleAdultId: v } })} placeholder="—" help={t('v2.legacy.helpers.Fill_later_adult_not_checkedin')} />
          <Select label={t('v2.legacy.labels.Child_relationship')} options={childOptions.map((o) => ({ id: o.id, label: o.name }))} value={rel.childRelationshipId} onChange={(v) => onChange({ relationship: { ...rel, childRelationshipId: v, adultRelationshipId: '' } })} placeholder="—" />
          <Select label={t('v2.legacy.labels.Adult_relationship')} options={adultOptions.map((o) => ({ id: o.id, label: o.name }))} value={rel.adultRelationshipId} onChange={(v) => onChange({ relationship: { ...rel, adultRelationshipId: v } })} placeholder="—" disabled={!rel.childRelationshipId} />
        </div>
      ) : age !== undefined ? (
        <Select key="relationship" label={holderName ? t('v2.checkin.ley.relacion', { name: holderName }) : t('v2.checkin.ley.relacionTitular')} labelNote={t('v2.checkin.ley.relacionNota')} options={[]} value="" onChange={() => undefined} placeholder="—" disabledReason={t('v2.checkin.ley.noHaceFalta', { count: age })} />
      ) : null
    ) : null,
  }
  return <div ref={rootRef}>{fields.map((f) => <div key={f} data-checkin-field={f}>{out[f]}</div>)}</div>
}
